import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { Upload, Edit, Trash2, Plus, Play, Check, X } from "lucide-react";

// Initial mock data
const initialStudents = [];
const historyStudents = [
    {
        id: 1,
        name: "Emily Chen",
        program: "LLM in American Law",
        country: "China",
        comments: "Interested in career opportunities in US firms",
        videoStatus: "Pending",
    },
    {
        id: 2,
        name: "Michael Rodriguez",
        program: "LLM in International Law",
        country: "Spain",
        comments: "Concerns about visa requirements",
        videoStatus: "Not Started",
    },
    {
        id: 3,
        name: "Sophia Lee",
        program: "LLM in Business Law",
        country: "South Korea",
        comments: "Inquiring about financial aid options",
        videoStatus: "Completed",
    },
];

const VideosPage = () => {
    const [students, setStudents] = useState(initialStudents);
    const [history, setHistory] = useState(historyStudents);
    const [editStudent, setEditStudent] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [editableData, setEditableData] = useState({});

    // Video status options
    const videoStatusOptions = [
        "Not Started",
        "Pending",
        "Failed",
        "Scripting",
        "Voicing",
        "Drafting",
        "Completed",
    ];

    // Update editable data when students change
    useEffect(() => {
        if (students.length > 0) {
            const newEditableData = {};
            students.forEach((student) => {
                newEditableData[student.id] = { ...student };
            });
            setEditableData(newEditableData);
        }
    }, [students]);

    // CSV Upload Handler
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        Papa.parse(file, {
            header: true,
            complete: (results) => {
                // Transform parsed CSV data to match our new student schema
                const parsedStudents = results.data
                    .map((row, index) => ({
                        id: students.length + index + 1,
                        name: row.name || "",
                        program: row.program || "",
                        country: row.country || "",
                        comments: row.comments || "",
                        videoStatus: "Not Started",
                    }))
                    .filter((student) => student.name); // Remove empty rows

                setStudents((prevStudents) => [
                    ...prevStudents,
                    ...parsedStudents,
                ]);
            },
            skipEmptyLines: true,
        });
    };

    // Add/Edit Student Handler
    const handleSaveStudent = () => {
        if (editStudent) {
            // Edit existing student
            setHistory((prevStudents) =>
                prevStudents.map((student) =>
                    student.id === editStudent.id ? editStudent : student
                )
            );
        } else {
            // Add new student
            setStudents((prevStudents) => [
                ...prevStudents,
                {
                    ...editStudent,
                    id: prevStudents.length + 1,
                },
            ]);
        }

        // Close modal and reset
        setIsAddModalOpen(false);
        setEditStudent(null);
    };

    // Delete Student Handler
    const handleDeleteStudent = () => {
        if (studentToDelete) {
            setHistory((prevStudents) =>
                prevStudents.filter(
                    (student) => student.id !== studentToDelete.id
                )
            );
            setDeleteConfirmOpen(false);
            setStudentToDelete(null);
        }
    };

    // Render Status Badge
    const renderStatusBadge = (status) => {
        const colorMap = {
            Completed: "bg-green-100 text-green-800",
            Pending: "bg-yellow-100 text-yellow-800",
            "Not Started": "bg-gray-100 text-gray-800",
            Failed: "bg-red-100 text-red-800",
            Scripting: "bg-blue-100 text-blue-800",
            Voicing: "bg-purple-100 text-purple-800",
            Drafting: "bg-indigo-100 text-indigo-800",
        };

        return (
            <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                    colorMap[status] || "bg-gray-100 text-gray-800"
                }`}
            >
                {status}
            </span>
        );
    };

    // Handle start processing videos
    const handleStartProcessing = () => {
        setIsProcessing(true);
        // Simulate progress over 5 minutes, stopping at 50%
        const interval = setInterval(() => {
            setProgress((prevProgress) => {
                const newProgress = prevProgress + 0.25;
                if (newProgress >= 50) {
                    clearInterval(interval);
                    return 50;
                }
                return newProgress;
            });
        }, 3000); // Update every 3 seconds to reach 50% in about 5 minutes
    };

    // Handle editable cell change
    const handleCellChange = (studentId, field, value) => {
        setEditableData((prev) => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value,
            },
        }));
    };

    // Apply changes from editable data to students
    const applyChanges = () => {
        const updatedStudents = students.map((student) => ({
            ...student,
            ...editableData[student.id],
        }));
        setStudents(updatedStudents);
    };

    return (
        <div>
            <div className="container mx-auto p-6 rounded-md mb-8 shadow-md bg-white">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                        Start Creating Videos!
                    </h1>

                    <div className="flex space-x-4">
                        <label className="cursor-pointer">
                            <div className="flex items-center px-4 py-2 bg-[#4880ff] text-white rounded-full hover:bg-[#3a6acd] transition-colors">
                                <Upload className="mr-2" size={20} />
                                Upload CSV
                            </div>
                            <input
                                type="file"
                                hidden
                                accept=".csv"
                                onChange={handleFileUpload}
                            />
                        </label>
                        <button
                            onClick={() => {
                                setEditStudent({
                                    name: "",
                                    program: "",
                                    country: "",
                                    comments: "",
                                    videoStatus: "Not Started",
                                });
                                setIsAddModalOpen(true);
                            }}
                            className="flex items-center px-4 py-2 border border-[#4880ff] text-[#4880ff] rounded-full hover:bg-[#4880ff]/10 transition-colors"
                        >
                            <Plus className="mr-2" size={20} />
                            Add Student
                        </button>
                    </div>
                </div>
                <div className="flex p-4 relative min-h-[220px]">
                    {students.length === 0 ? (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center items-center gap-3">
                            <VideoCallIcon
                                style={{ fontSize: "85px" }}
                                className="text-gray-400"
                            />
                            <span className="text-center text-2xl font-bold text-gray-400">
                                Upload student data to start <br /> creating
                                videos!
                            </span>
                        </div>
                    ) : (
                        <div className="w-full flex flex-col">
                            {/* Editable Excel-like Block */}
                            <div className="mb-4 border rounded-lg overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            {[
                                                "Name",
                                                "Program",
                                                "Country",
                                                "Comments",
                                            ].map((header) => (
                                                <th
                                                    key={header}
                                                    className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider"
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                            {!isProcessing && (
                                                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {students.map((student) => (
                                            <tr
                                                key={student.id}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                    {isProcessing ? (
                                                        editableData[student.id]
                                                            ?.name
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={
                                                                editableData[
                                                                    student.id
                                                                ]?.name || ""
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(
                                                                    student.id,
                                                                    "name",
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#4880ff]/50"
                                                            disabled={
                                                                isProcessing
                                                            }
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                    {isProcessing ? (
                                                        editableData[student.id]
                                                            ?.program
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={
                                                                editableData[
                                                                    student.id
                                                                ]?.program || ""
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(
                                                                    student.id,
                                                                    "program",
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#4880ff]/50"
                                                            disabled={
                                                                isProcessing
                                                            }
                                                        />
                                                    )}
                                                </td>

                                                <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                    {isProcessing ? (
                                                        editableData[student.id]
                                                            ?.country
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={
                                                                editableData[
                                                                    student.id
                                                                ]?.country || ""
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(
                                                                    student.id,
                                                                    "country",
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#4880ff]/50"
                                                            disabled={
                                                                isProcessing
                                                            }
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 whitespace-nowrap text-xs">
                                                    {isProcessing ? (
                                                        editableData[student.id]
                                                            ?.comments || "-"
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={
                                                                editableData[
                                                                    student.id
                                                                ]?.comments ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(
                                                                    student.id,
                                                                    "comments",
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className="w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#4880ff]/50"
                                                            disabled={
                                                                isProcessing
                                                            }
                                                        />
                                                    )}
                                                </td>
                                                {!isProcessing && (
                                                    <td className="px-2 py-2 whitespace-nowrap">
                                                        <div className="flex space-x-1">
                                                            <button
                                                                onClick={() => {
                                                                    const updatedStudents =
                                                                        students.filter(
                                                                            (
                                                                                s
                                                                            ) =>
                                                                                s.id !==
                                                                                student.id
                                                                        );
                                                                    setStudents(
                                                                        updatedStudents
                                                                    );
                                                                }}
                                                                className="text-red-600 hover:text-red-900 transition-colors"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Start Button or Progress Bar */}
                            <div className="flex justify-end mt-auto">
                                {!isProcessing ? (
                                    <button
                                        onClick={() => {
                                            applyChanges();
                                            handleStartProcessing();
                                        }}
                                        className="flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                                    >
                                        <Play className="mr-2" size={18} />
                                        Start Processing
                                    </button>
                                ) : (
                                    <div className="w-full flex items-center gap-4 justify-center">
                                        <div className="flex-grow">
                                            <div className="flex justify-between mb-1">
                                                <span className="text-xs font-semibold">
                                                    Processing videos...
                                                </span>
                                                <span className="text-xs font-semibold">
                                                    {progress.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${progress}%`,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                setIsProcessing(false)
                                            }
                                            className="px-2 py-1 border border-red-500 text-red-500 rounded-full hover:bg-red-50 transition-colors text-sm"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="container mx-auto p-6 shadow-md rounded-md bg-white">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                        History
                    </h1>
                </div>

                {/* Table */}
                <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                {[
                                    "Name",
                                    "Program",
                                    "Country",
                                    "Comments",
                                    "Video Status",
                                    "Actions",
                                ].map((header) => (
                                    <th
                                        key={header}
                                        className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {history.map((student) => (
                                <tr
                                    key={student.id}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                        {student.name}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                        {student.program}
                                    </td>

                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                        {student.country}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                        {student.comments || "-"}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs">
                                        {renderStatusBadge(student.videoStatus)}
                                    </td>
                                    <td className="px-2 py-2 whitespace-nowrap">
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={() => {
                                                    setStudentToDelete(student);
                                                    setDeleteConfirmOpen(true);
                                                }}
                                                className="text-red-600 hover:text-red-900 transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Add/Edit Student Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                            <h2 className="text-xl font-bold mb-4">
                                {editStudent?.id
                                    ? "Edit Student"
                                    : "Add New Student"}
                            </h2>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={editStudent?.name || ""}
                                            onChange={(e) =>
                                                setEditStudent((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Program
                                        </label>
                                        <input
                                            type="text"
                                            value={editStudent?.program || ""}
                                            onChange={(e) =>
                                                setEditStudent((prev) => ({
                                                    ...prev,
                                                    program: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            value={editStudent?.country || ""}
                                            onChange={(e) =>
                                                setEditStudent((prev) => ({
                                                    ...prev,
                                                    country: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Comments
                                        </label>
                                        <input
                                            type="text"
                                            value={editStudent?.comments || ""}
                                            onChange={(e) =>
                                                setEditStudent((prev) => ({
                                                    ...prev,
                                                    comments: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Video Status
                                    </label>
                                    <select
                                        value={
                                            editStudent?.videoStatus ||
                                            "Not Started"
                                        }
                                        onChange={(e) =>
                                            setEditStudent((prev) => ({
                                                ...prev,
                                                videoStatus: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4880ff]/50"
                                    >
                                        {videoStatusOptions.map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex justify-end space-x-4 mt-4">
                                    <button
                                        onClick={() => {
                                            setIsAddModalOpen(false);
                                            setEditStudent(null);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveStudent}
                                        className="px-4 py-2 bg-[#4880ff] text-white rounded-full hover:bg-[#3a6acd] transition-colors"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirmOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                            <h2 className="text-xl font-bold mb-4 text-red-600">
                                Confirm Deletion
                            </h2>
                            <p className="mb-6 text-gray-700">
                                Are you sure you want to delete the student{" "}
                                <span className="font-semibold">
                                    {studentToDelete?.name}
                                </span>
                                ?
                            </p>
                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={() => {
                                        setDeleteConfirmOpen(false);
                                        setStudentToDelete(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteStudent}
                                    className="px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideosPage;
